/**
 * Variant trackers (B8 / v1.1.0-b1).
 *
 * Two exported components:
 *  - <VariantGrid speciesId={n} /> mounts inside the species-detail sheet.
 *    Renders nothing when the species has no catalog entries, so it is
 *    safe to mount unconditionally.
 *  - <VariantProgressCard /> mounts at the bottom of the Dex page.
 *    Shows a small rollup: "Vivillon 8/20", "Unown 14/28",
 *    "Regional forms 23/58".
 *
 * Pro gating: variant tracking is a Pro feature per the rescope plan.
 *   Basic users see the grid + their server-known caught state, but
 *   any tap-to-log opens the IAP paywall ("Unlock variant tracking
 *   with Pro").
 *   Pro users can tap-to-log; the server POST /api/dex/variants writes
 *   the row and React Query invalidates so the tile flips immediately.
 *
 * OCR is out of scope for B8 (variant detection from a PoGo screen is
 * its own can of worms). Trainers manually tap the tile they caught.
 */
import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Globe2, Lock, MapPin, Sparkles, Check, Star, AlertCircle } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// ── Types from the server ───────────────────────────────────────────────────

type VariantType = "vivillon_pattern" | "unown_letter" | "regional_form" | "shiny";

interface VariantEntry {
  id: number;
  speciesId: number;
  variantType: VariantType;
  variantKey: string;
  displayName: string;
  regionLock: string | null;
  rarity: string | null;
  caught: boolean;
  caughtAt: number | null;
  source: string | null;
}

interface SpeciesVariantsResponse {
  speciesId: number;
  entries: VariantEntry[];
  summary: { caught: number; total: number };
}

interface VariantProgressResponse {
  byType: Array<{ variantType: VariantType; caught: number; total: number }>;
}

// Pretty labels for the progress card header.
const VARIANT_TYPE_LABEL: Record<VariantType, string> = {
  vivillon_pattern: "Vivillon patterns",
  unown_letter: "Unown letters",
  regional_form: "Regional forms",
  shiny: "Shiny tracker",
};

const VARIANT_TYPE_SHORT: Record<VariantType, string> = {
  vivillon_pattern: "Vivillon",
  unown_letter: "Unown",
  regional_form: "Regional forms",
  shiny: "Shiny",
};

// ── VariantGrid: species-detail block ──────────────────────────────────────

export function VariantGrid({ speciesId }: { speciesId: number }) {
  const { toast } = useToast();

  const q = useQuery<SpeciesVariantsResponse>({
    queryKey: ["/api/dex/variants", speciesId],
  });

  const logMutation = useMutation({
    mutationFn: async (entry: VariantEntry) => {
      const res = await apiRequest("POST", "/api/dex/variants", {
        speciesId: entry.speciesId,
        variantType: entry.variantType,
        variantKey: entry.variantKey,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dex/variants", speciesId] });
      queryClient.invalidateQueries({ queryKey: ["/api/dex/variants/progress"] });
    },
    onError: (err: any) => {
      toast({
        title: "Could not log variant",
        description: err?.message ?? "Try again in a moment.",
        variant: "destructive",
      });
    },
  });

  // Empty species (most of them) -> render nothing so the detail sheet
  // does not show a stray heading.
  if (q.isLoading) return null;
  if (q.error) return null;
  const entries = q.data?.entries ?? [];
  if (entries.length === 0) return null;

  function handleTap(entry: VariantEntry) {
    if (entry.caught) {
      // Show the catch date inline. We do not implement an Undo here in
      // the v1 UI; that lives in the variant detail flow.
      const when = entry.caughtAt ? new Date(entry.caughtAt).toLocaleDateString() : null;
      toast({
        title: entry.displayName,
        description: when ? `Logged on ${when}.` : "Already logged.",
      });
      return;
    }
    // Free on every tier since 2026-09-11 (owner: "REMOVE this blocker").
    logMutation.mutate(entry);
  }

  // Group by variant_type so a species with two distinct trackers (e.g.
  // shiny + regional) renders two sub-blocks in order.
  const byType = new Map<VariantType, VariantEntry[]>();
  for (const e of entries) {
    const list = byType.get(e.variantType) ?? [];
    list.push(e);
    byType.set(e.variantType, list);
  }

  return (
    <section data-testid={`variant-grid-${speciesId}`}>
      <p
        className="v2-display-up text-[10px] mb-2 inline-flex items-center gap-1.5"
        style={{ color: "var(--accent-fill)" }}
      >
        <Sparkles className="w-3 h-3" /> Variants
      </p>
      {Array.from(byType.entries()).map(([type, list]) => (
        <VariantSubGroup
          key={type}
          type={type}
          entries={list}
          onTap={handleTap}
          pro
        />
      ))}
    </section>
  );
}

function VariantSubGroup({
  type,
  entries,
  onTap,
  pro,
}: {
  type: VariantType;
  entries: VariantEntry[];
  onTap: (entry: VariantEntry) => void;
  pro: boolean;
}) {
  const caught = entries.filter((e) => e.caught).length;
  return (
    <div className="mb-4" data-testid={`variant-subgroup-${type}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11.5px] font-semibold text-foreground/90">
          {VARIANT_TYPE_LABEL[type]}
        </p>
        <span
          className="font-mono text-[10px] tabular-nums px-1.5 py-0.5 rounded"
          style={{
            color: "hsl(var(--primary))",
            background: "hsl(var(--primary) / 0.12)",
            border: "1px solid hsl(var(--primary) / 0.3)",
          }}
        >
          {caught}/{entries.length}
        </span>
      </div>
      <div
        className={
          type === "unown_letter"
            ? "grid grid-cols-7 gap-1.5"
            : "grid grid-cols-4 gap-2"
        }
      >
        {entries.map((entry) => (
          <VariantTile
            key={entry.id}
            entry={entry}
            onTap={() => onTap(entry)}
            proLocked={!pro && !entry.caught}
          />
        ))}
      </div>
    </div>
  );
}

function VariantTile({
  entry,
  onTap,
  proLocked,
}: {
  entry: VariantEntry;
  onTap: () => void;
  proLocked: boolean;
}) {
  const caught = entry.caught;
  const isUnown = entry.variantType === "unown_letter";
  // Short label inside the tile. Unown shows its letter; regional forms
  // and Vivillon patterns show their key in caps (truncated for fit).
  const tileLabel = isUnown
    ? entry.variantKey === "exclaim"
      ? "!"
      : entry.variantKey === "question"
        ? "?"
        : entry.variantKey.toUpperCase()
    : entry.displayName.split(" ").slice(-1)[0];

  return (
    <button
      type="button"
      data-testid={`variant-tile-${entry.variantType}-${entry.variantKey}`}
      onClick={onTap}
      className={
        isUnown
          ? "aspect-square rounded-lg flex flex-col items-center justify-center transition relative"
          : "aspect-square rounded-xl flex flex-col items-center justify-center px-1.5 py-1 transition relative"
      }
      style={{
        background: caught
          ? "linear-gradient(135deg, var(--accent-fill-glow-faint), var(--surface-elev-1) 70%)"
          : "var(--surface-elev-2)",
        border: caught
          ? "1px solid var(--accent-fill-glow)"
          : proLocked
            ? "1px dashed var(--surface-elev-border)"
            : "1px solid var(--surface-elev-border)",
        opacity: caught ? 1 : proLocked ? 0.55 : 0.85,
      }}
      title={entry.displayName + (entry.regionLock ? ` (${entry.regionLock})` : "")}
    >
      <span
        className={
          isUnown
            ? "font-mono text-base font-bold leading-none"
            : "text-[10.5px] font-semibold leading-tight text-center"
        }
        style={{
          color: caught ? "var(--accent-fill)" : "hsl(var(--foreground) / 0.8)",
        }}
      >
        {tileLabel}
      </span>
      {!isUnown && entry.regionLock && (
        <span
          className="text-[8.5px] mt-0.5 leading-none truncate max-w-full"
          style={{ color: "var(--text-ink-muted)" }}
        >
          {entry.regionLock.split(" ")[0]}
        </span>
      )}
      {caught && (
        <Check
          className="absolute top-0.5 right-0.5 w-3 h-3"
          style={{ color: "var(--accent-fill)" }}
        />
      )}
      {!caught && proLocked && (
        <Lock
          className="absolute top-0.5 right-0.5 w-2.5 h-2.5 opacity-60"
          style={{ color: "hsl(var(--muted-foreground))" }}
        />
      )}
    </button>
  );
}

// ── VariantProgressCard: Dex page footer ───────────────────────────────────

export function VariantProgressCard() {
  const q = useQuery<VariantProgressResponse>({
    queryKey: ["/api/dex/variants/progress"],
  });

  const rows = q.data?.byType ?? [];
  // Hide entirely until catalog is reachable; on error we show a soft
  // "could not load" line so the trainer is not left guessing.
  if (q.isLoading) {
    return (
      <section
        className="mt-5 rounded-2xl p-4 animate-pulse"
        aria-label="Loading variant progress"
        style={{
          background: "var(--surface-elev-1)",
          border: "1px solid var(--surface-elev-border)",
        }}
      >
        <div className="h-2 w-20 rounded bg-muted-foreground/15 mb-2" />
        <div className="h-3 w-32 rounded bg-muted-foreground/15" />
      </section>
    );
  }
  if (q.error) {
    return (
      <section
        className="mt-5 rounded-2xl p-4 flex items-start gap-2"
        style={{
          background: "var(--surface-elev-1)",
          border: "1px solid var(--surface-elev-border)",
        }}
        data-testid="variant-progress-error"
      >
        <AlertCircle className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
        <p className="text-[12px] text-muted-foreground">
          Could not load variant progress. Pull to refresh.
        </p>
      </section>
    );
  }
  if (rows.length === 0) return null;

  const total = rows.reduce((s, r) => s + r.total, 0);
  const caught = rows.reduce((s, r) => s + r.caught, 0);

  return (
    <section
      className="mt-5 rounded-2xl p-4"
      data-testid="variant-progress-card"
      style={{
        background: "var(--surface-elev-1)",
        border: "1px solid var(--surface-elev-border)",
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, hsl(var(--primary) / 0.18), hsl(var(--primary) / 0.04))",
              border: "1px solid hsl(var(--primary) / 0.35)",
              color: "hsl(var(--primary))",
            }}
          >
            <Globe2 className="w-4 h-4" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[hsl(var(--primary))]">
              Ultimate dex
            </p>
            <p className="text-sm font-semibold">
              Variant collection
            </p>
          </div>
        </div>
        <span
          className="font-mono text-[11px] tabular-nums px-1.5 py-0.5 rounded"
          style={{
            color: "hsl(var(--primary))",
            background: "hsl(var(--primary) / 0.12)",
            border: "1px solid hsl(var(--primary) / 0.3)",
          }}
        >
          {caught}/{total}
        </span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <VariantProgressRow
            key={r.variantType}
            label={VARIANT_TYPE_SHORT[r.variantType]}
            caught={r.caught}
            total={r.total}
          />
        ))}
      </div>
      <p className="mt-3 text-[10.5px] text-muted-foreground text-center">
        Tap any species to log the pattern you caught.
      </p>
    </section>
  );
}

function VariantProgressRow({
  label,
  caught,
  total,
}: {
  label: string;
  caught: number;
  total: number;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((caught / total) * 100)) : 0;
  return (
    <div className="flex items-center gap-3" data-testid={`variant-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[11.5px] font-semibold flex-1 truncate">{label}</p>
      <div
        className="h-1.5 w-20 rounded-full overflow-hidden"
        style={{ background: "var(--surface-elev-2)" }}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, var(--accent-fill), var(--accent-fill))",
          }}
        />
      </div>
      <span className="font-mono text-[10.5px] tabular-nums shrink-0 w-12 text-right text-muted-foreground">
        {caught}/{total}
      </span>
    </div>
  );
}
